import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileSignature, ShieldCheck, Download, Loader2, CheckCircle2, Clock, PenLine, Lock, Eraser, Type } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const cv = ref.current;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.strokeStyle = "#e4e4e7";
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    let drawing = false;
    let hasInk = false;
    const point = (e) => {
      const r = cv.getBoundingClientRect();
      return [(e.clientX - r.left) * (cv.width / r.width), (e.clientY - r.top) * (cv.height / r.height)];
    };
    const start = (e) => {
      e.preventDefault();
      try { cv.setPointerCapture(e.pointerId); } catch { /* noop */ }
      drawing = true;
      const [x, y] = point(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (e) => {
      if (!drawing) return;
      e.preventDefault();
      const [x, y] = point(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      hasInk = true;
    };
    const end = () => {
      if (drawing && hasInk) onChangeRef.current(cv.toDataURL("image/png"));
      drawing = false;
    };
    cv.addEventListener("pointerdown", start, { passive: false });
    cv.addEventListener("pointermove", move, { passive: false });
    cv.addEventListener("pointerup", end);
    cv.addEventListener("pointercancel", end);
    cv.addEventListener("pointerleave", end);
    return () => {
      cv.removeEventListener("pointerdown", start);
      cv.removeEventListener("pointermove", move);
      cv.removeEventListener("pointerup", end);
      cv.removeEventListener("pointercancel", end);
      cv.removeEventListener("pointerleave", end);
    };
  }, []);

  const clear = () => {
    const cv = ref.current;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, cv.width, cv.height);
    onChangeRef.current("");
  };

  return (
    <div className="flex flex-col gap-2">
      <canvas
        ref={ref} width={640} height={200} data-testid="signature-canvas"
        className="w-full border border-white/20 bg-[#0a0a0a] cursor-crosshair select-none"
        style={{ height: 140, touchAction: "none", WebkitUserSelect: "none" }}
      />
      <div className="flex items-center justify-between">
        <span className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.2em]">Draw your signature — mouse or finger</span>
        <button type="button" data-testid="signature-clear" onClick={clear} className="font-mono text-[8px] uppercase text-zinc-500 hover:text-zinc-200 flex items-center gap-1 transition-colors"><Eraser className="w-3 h-3" /> Clear</button>
      </div>
    </div>
  );
}

const STATUS_META = {
  sent: { label: "Awaiting review", cls: "text-zinc-400 border-white/20" },
  viewed: { label: "Reviewed — awaiting signature", cls: "text-nc-blue border-nc-blue/40" },
  signed: { label: "Signed — awaiting studio countersignature", cls: "text-nc-orange border-nc-orange/40" },
  completed: { label: "Fully executed", cls: "text-nc-green border-nc-green/40" },
};

export default function ContractSignPage() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [sigMode, setSigMode] = useState("draw");
  const [sigImage, setSigImage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/contracts/sign/${token}`).then((r) => setContract(r.data)).catch((e) => setError(e.response?.data?.detail || "Contract not found or link expired"));
  }, [token]);

  const sign = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/contracts/sign/${token}`, { signer_name: name, consent: true, signature_image: sigImage });
      toast.success(r.data.message);
      const refreshed = await api.get(`/contracts/sign/${token}`);
      setContract(refreshed.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const meta = contract ? STATUS_META[contract.status] || STATUS_META.sent : null;

  return (
    <div data-testid="contract-sign-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex items-center gap-3">
          <FileSignature className="w-5 h-5 text-nc-orange" />
          <div>
            <p className="font-sans font-bold uppercase text-sm text-zinc-50">Specpixel Entertainment</p>
            <p className="font-mono text-[8px] tracking-[0.3em] text-zinc-500 uppercase">Secure contract signing</p>
          </div>
          {contract && <span data-testid="contract-status" className={`ml-auto font-mono text-[9px] tracking-[0.2em] uppercase border px-3 py-1.5 ${meta.cls}`}>{meta.label}</span>}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 w-full flex-1">
        {error && (
          <div data-testid="contract-error" className="border border-red-500/40 bg-red-500/5 p-10 text-center">
            <Lock className="w-8 h-8 text-red-400 mx-auto mb-4" />
            <p className="font-mono text-sm text-zinc-300">{error}</p>
            <Link to="/" className="font-mono text-[10px] text-nc-blue mt-4 inline-block">Back to specpixel.com</Link>
          </div>
        )}

        {!contract && !error && (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 text-nc-blue animate-spin" /></div>
        )}

        {contract && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="flex flex-col gap-6">
            {/* document */}
            <div data-testid="contract-body" className="border border-white/10 bg-[#0a0a0a] p-6 sm:p-10">
              <div className="border-b border-white/10 pb-5 mb-6">
                <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">Contract {contract.id.slice(0, 8)} · {contract.project}</p>
                <h1 className="font-sans font-bold uppercase text-xl text-zinc-50 mt-2">{contract.contract_type === "nda" ? "Mutual Non-Disclosure Agreement" : "Software Development Services Agreement"}</h1>
                <p className="font-mono text-[10px] text-zinc-500 mt-1.5">Between <b className="text-zinc-300">{contract.company}</b> and <b className="text-zinc-300">{contract.party_name}</b> — {contract.role_title}</p>
              </div>
              <pre className="font-mono text-[11px] text-zinc-400 leading-relaxed whitespace-pre-wrap">{contract.body}</pre>

              {/* signature blocks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 pt-6 border-t border-white/10">
                <div className="border border-white/10 p-4">
                  <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase mb-2">Contractor</p>
                  {contract.signature ? (
                    <div>
                      {contract.signature.image ? (
                        <img src={contract.signature.image} alt="signature" className="h-10 invert opacity-90" />
                      ) : (
                        <p className="font-sans italic text-lg text-zinc-100">/s/ {contract.signature.name}</p>
                      )}
                      <p className="font-mono text-[8px] text-zinc-500 mt-1">{String(contract.signature.at).slice(0, 19)}Z · IP {contract.signature.ip}</p>
                    </div>
                  ) : (
                    <p className="font-mono text-[10px] text-zinc-600 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Awaiting signature</p>
                  )}
                </div>
                <div className="border border-white/10 p-4">
                  <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase mb-2">Studio countersignature</p>
                  {contract.countersign ? (
                    <div>
                      <p className="font-sans italic text-lg text-zinc-100">/s/ {contract.countersign.name}</p>
                      <p className="font-mono text-[8px] text-zinc-500 mt-1">{String(contract.countersign.at).slice(0, 19)}Z · IP {contract.countersign.ip}</p>
                    </div>
                  ) : (
                    <p className="font-mono text-[10px] text-zinc-600 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> After contractor signs</p>
                  )}
                </div>
              </div>
            </div>

            {/* action bar */}
            <div className="border border-white/10 bg-[#070707] p-6 flex flex-col gap-4">
              <div className="flex items-center gap-2 font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase flex-wrap">
                <ShieldCheck className="w-4 h-4 text-nc-green" /> ESIGN Act + Electronic Transactions Act 1999 compliant · full audit trail ·
                <a data-testid="contract-pdf" href={`${process.env.REACT_APP_BACKEND_URL}/api/contracts/sign/${token}/pdf`} className="text-nc-blue hover:text-zinc-100 inline-flex items-center gap-1 transition-colors"><Download className="w-3.5 h-3.5" /> Download PDF</a>
              </div>

              {["sent", "viewed"].includes(contract.status) && (
                <div data-testid="sign-panel" className="flex flex-col gap-3">
                  <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">Type your full legal name, then draw your signature (or switch to typed). Either is a legally binding electronic signature.</p>
                  <input data-testid="sign-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Your full legal name" className="bg-[#050505] border border-white/10 px-3 py-3 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue" />
                  <div className="flex gap-1.5">
                    <button data-testid="sign-mode-draw" onClick={() => setSigMode("draw")} className={`font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-2 border transition-colors flex items-center gap-1.5 ${sigMode === "draw" ? "border-nc-blue text-nc-blue bg-nc-blue/10" : "border-white/15 text-zinc-500"}`}><PenLine className="w-3.5 h-3.5" /> Draw</button>
                    <button data-testid="sign-mode-type" onClick={() => { setSigMode("type"); setSigImage(""); }} className={`font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-2 border transition-colors flex items-center gap-1.5 ${sigMode === "type" ? "border-nc-blue text-nc-blue bg-nc-blue/10" : "border-white/15 text-zinc-500"}`}><Type className="w-3.5 h-3.5" /> Typed</button>
                  </div>
                  {sigMode === "draw" ? (
                    <SignaturePad onChange={setSigImage} />
                  ) : (
                    name.trim().length >= 2 && <p className="font-sans italic text-3xl text-zinc-100 border-b border-white/20 pb-2">/s/ {name}</p>
                  )}
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input data-testid="sign-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#FF6B1A]" />
                    <span className="font-mono text-[9px] text-zinc-400 leading-relaxed">I consent to sign electronically and agree my signature, timestamp and IP address form my legal signature on this agreement.</span>
                  </label>
                  <button data-testid="sign-submit" disabled={busy || name.trim().length < 2 || !consent || (sigMode === "draw" && !sigImage)} onClick={sign} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-mono text-[10px] tracking-[0.25em] uppercase px-6 py-3.5 transition-colors flex items-center justify-center gap-2 self-start">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Sign Contract
                  </button>
                </div>
              )}
              {contract.status === "signed" && (
                <div data-testid="signed-notice" className="border border-nc-orange/40 bg-nc-orange/5 p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-nc-orange shrink-0" />
                  <p className="font-mono text-[10px] text-zinc-300 leading-relaxed">Signed and locked with your audit trail. The studio countersigns next — you'll receive the fully executed copy by email.</p>
                </div>
              )}
              {contract.status === "completed" && (
                <div data-testid="completed-notice" className="border border-nc-green/40 bg-nc-green/5 p-4 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-nc-green shrink-0" />
                  <p className="font-mono text-[10px] text-zinc-300 leading-relaxed">Fully executed by both parties. Download the PDF above for your permanent records.</p>
                </div>
              )}
            </div>

            {/* audit trail */}
            {(contract.events || []).length > 0 && (
              <div data-testid="contract-events" className="border border-white/10 bg-[#070707] p-5">
                <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase mb-3">Audit trail</p>
                <div className="flex flex-col gap-1.5">
                  {contract.events.map((e, i) => (
                    <p key={i} className="font-mono text-[9px] text-zinc-500 flex gap-3"><span className="text-zinc-600 shrink-0">{String(e.at).slice(0, 19)}Z</span><span className="text-zinc-300 uppercase text-[8px] tracking-[0.15em] w-24 shrink-0">{e.kind}</span><span>{e.detail}</span></p>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
}
